import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { useSelector } from "react-redux";
import { CountryCode } from "../../../constants/countryCurrency";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";
am5.addLicense("AM5C258896422");

const StackedColumnChart = ({
    chartId,
    chartClass,
    showLegend,
    showLabels,
    chartData,
    chartLabels,
    tooltipMaxWidth
}) => {
    const userData = useSelector((state) => state.userData.user[0]);
    let userCountryCode = userData?.country_code ?? "IN"
    let country = {};
    CountryCode.map((data) => {
        if (data.countryCode === userCountryCode) {
            country = data
        }
    })

    useLayoutEffect(() => {
        var root = am5.Root.new(chartId);

        root.numberFormatter.setAll({
            numberFormat: "#.##a",
            bigNumberPrefixes: [
                { number: 1e3, suffix: "K" },
                { number: 1e6, suffix: "M" },
                { number: 1e9, suffix: "B" },
            ],
            smallNumberPrefixes: [],
        });

        root.setThemes([am5themes_Animated.new(root)]);
        root.interfaceColors.set("grid", am5.color(0xffffff));

        var chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                layout: root.verticalLayout,
                paddingRight: 0,
                paddingTop: 5,
                paddingBottom: 0,
                paddingLeft: 0,
                maxTooltipDistance: -1,
            })
        );

        chart.get("colors").set("colors", [
            am5.color(0x027BC7),
            am5.color(0x11a1fd),
            //    am5.color(0x91C4FA),

            am5.color(0x00558A),
            am5.color(0x005991),
            am5.color(0x005D97),
            am5.color(0x066BA9),
            am5.color(0x0871B3),
            am5.color(0x0375BD),
            am5.color(0x027BC7),
            am5.color(0x0285D6),
            am5.color(0x028ADE),
            am5.color(0x0293ED),
            am5.color(0x039DFC),
            am5.color(0x2DADFD),
            am5.color(0x3AB2FD),
            am5.color(0x48B8FD),
            am5.color(0x55BDFD),
            am5.color(0x63C2FD),
            am5.color(0x71C8FE),
            am5.color(0x7ECDFE),
            am5.color(0x8CD2FE),
            am5.color(0xC2E7FE),
            am5.color(0xD3EEFF),
            am5.color(0xE0F3FF),
            am5.color(0xE8F6FF),
        ]);

        var cursor = chart.set(
            "cursor",
            am5xy.XYCursor.new(root, {
                behavior: "zoomXY",
            })
        );
        cursor.lineY.set("visible", false);
        cursor.lineX.set("visible", false);

        var data = chartData;

        // X Axis (rotated labels)
        var xRenderer = am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
            cellStartLocation: 0.4,
            cellEndLocation: 0.6,
            minGridDistance: 50,
            stroke: am5.color(0x707070),
            strokeWidth: 1,
            strokeOpacity: 1,
        });
        xRenderer.labels.template.setAll({
            rotation: -45,
            centerY: am5.p50,
            centerX: am5.p100,
            paddingRight: 15,
            oversizedBehavior: "wrap",
            maxWidth: 120,
            fontSize: 12,
            fontFamily: "Ubuntu",
            // fill: "#757575",
            fill: am5.color(0x757575),
            fontWeight: "400"
        });

        var xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "label",
                renderer: xRenderer,
            })
        );

        if (showLabels === false) {
            xAxis.get("renderer").labels.template.setAll({
                visible: false,
            });
        }

        xAxis.data.setAll(data);

        // Y Axis (fix value display)
        var yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {
                    stroke: am5.color(0x707070),
                    strokeWidth: 1,
                    strokeOpacity: 1
                }),
                min: 0
            })
        );
        if (showLabels === false) {
            yAxis.get("renderer").labels.template.setAll({
                visible: false,
            });
        } else {
            yAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "truncate",
                maxWidth: 150,
                textAlign: "center",
                fontSize: 12,
                fontFamily: "Ubuntu",
                // fill: "#757575",
                fill: am5.color(0x757575),        // label color (same as Waterfall)

                fontWeight: "400",
            });
        }

        // Legend
        var legend = chart.children.push(
            am5.Legend.new(root, {
                position: "relative",
                x: am5.percent(50),
                centerX: am5.percent(50),
                height: chartLabels.length >= 5 ? am5.percent(30) : null,
                marginTop: 15,
                layout: am5.GridLayout.new(root, {
                    fixedWidthGrid: true,
                }),
                verticalScrollbar: am5.Scrollbar.new(root, {
                    orientation: "vertical",
                }),
            })
        );

        legend.markers.template.setAll({
            width: 12,
            height: 12,
            strokeWidth: 2,
            fillOpacity: 1,
            strokeOpacity: 1,
            centerY: am5.p50,
            centerX: am5.p50,
        });
        legend.markerRectangles.template.setAll({
            cornerRadiusTL: 2,
            cornerRadiusTR: 2,
            cornerRadiusBL: 2,
            cornerRadiusBR: 2,
        });
        legend.labels.template.setAll({
            // fontSize: 12,
            // fontWeight: "400",
            // fill: "#213345",
            // oversizedBehavior: "wrap",
            fontFamily: "Ubuntu, sans-serif",
            fontSize: 12,
            fill: am5.color(0x213345), // navy blue
            fontWeight: "400",
            oversizedBehavior: "wrap",
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 5,
            paddingRight: 5,
        });

        // Series
        function createSeries(name, field, childLabel, childValue, index) {
            var series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    // name: name,
                    name: name ? name.toUpperCase() : "", // <-- Force uppercase here

                    stacked: true,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: field,
                    categoryXField: "label",
                    childLabel: childLabel,
                    childValue: childValue,
                    columnWidth: 50,
                })
            );

            //             series.columns.template.setAll({
            //   strokeWidth: 0.5,
            //   stroke: am5.color(0xffffff), // White border, change as needed
            // });

            var tooltip = series.set(
                "tooltip",
                am5.Tooltip.new(root, {
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    autoTextColor: false,
                    pointerOrientation: "horizontal",
                    position: "pointer",
                })
            );

            tooltip.get("background").setAll({
                fill: am5.color(0x213345),
                maxWidth: tooltipMaxWidth ? tooltipMaxWidth : 200,
                oversizedBehavior: "truncate",
                cornerRadius: 5,
            });

            tooltip.label.setAll({
                text: "[bold]{categoryX}[/]",
                fill: am5.color(0xffffff),
                fontSize: 12,
                maxWidth: 300,
                oversizedBehavior: "wrap",
                fontFamily: "Ubuntu",
            });

            tooltip.label.adapters.add("text", function (text, target) {
                chart.series.each(function (series) {
                    var tooltipDataItem = series?.get("tooltipDataItem");
                    text += "\n[" + series.get("stroke").toString() + "]●[/] " + "[bold]" + series.get("name") + ":[/]" + " " + "[bold]" + formatNumberForTooltip(tooltipDataItem?.get("valueY"), series.get("name")) + "[/]";
                    if (series.get("childLabel") != undefined) {
                        text += "\n[" + series.get("stroke").toString() + "]●[/] " + "[bold]" + series.get("childLabel") + ":[/]" + " " + "[bold]" + formatNumberForTooltip(tooltipDataItem?.dataContext[series.get('childValue')], series.get("childLabel")) + "[/]";
                    }
                });
                return text;
            });

            series.data.setAll(data);
            series.appear(1000);
            if (showLegend === true) {
                legend.data.push(series);
            }
        }

        chartLabels.map((label, index) => {
            createSeries(
                label.label,
                label.value,
                label.childLabel,
                label.childValue,
                index
            );
        });

        // Safely apply custom legend style
        if (legend && legend.dom) {
            legend.dom.classList.add('custom-legend-style');
        }

        chart.zoomOutButton.set("forceHidden", true);
        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [chartData, chartLabels]);

    return (
        <Wrapper>
            {/* <div id={chartId} className={chartClass}></div> */}
            <div id={chartId} className={chartClass} style={{
                height: "340px",
                minHeight: "340px",
                background: "#fff",
                borderRadius: "12px"
            }}></div>
        </Wrapper>
    );
};

StackedColumnChart.propTypes = {
    chartId: PropTypes.string,
    chartClass: PropTypes.string,
    showLegend: PropTypes.bool,
    showLabels: PropTypes.bool,
    chartData: PropTypes.array,
    chartLabels: PropTypes.array,
    tooltipMaxWidth: PropTypes.string,
};

export default StackedColumnChart;